import { useEffect, useReducer, useState, useCallback } from 'react'
import { getNotificationsPage } from './queries/get-notifications-page-query'
import { connector } from '../../../api/connector'
import { NotificationData } from '../shared'
import { useContrastQuery } from '../../../hooks/use-contrast-query'
import { useContrastMutation } from '../../../hooks/use-contrast-mutation'
import { useQueryClient } from 'react-query'
import { updateNotificationRead } from './queries/update-notification-read'
import { Query } from '../../../hooks/queries'
import { getNewNotifications } from './queries/get-new-notifications-query'

export interface UseNotificationsPageQueryProps {
  organizationUuid: string
  enabled: boolean
}

export interface State {
  remoteCount: number
  cachedNotifications: NotificationData[]
  isLoading: boolean
  pageRequestError: boolean
  lastNotificationTimestamp: null | number
  markAllAsReadTimestamp: null | number
}

const initialState: State = {
  remoteCount: 0,
  cachedNotifications: [],
  isLoading: false,
  pageRequestError: false,
  lastNotificationTimestamp: null,
  markAllAsReadTimestamp: null
}

export interface MarkNotificationReadProps {
  notificationId: string
  onErrorHandler?: (e: Error) => void
}

export type UseNotificationsDataResult = Pick<
  State,
  'markAllAsReadTimestamp'
> & {
  notifications: NotificationData[]
  remoteCount: number
  isLoading: boolean
  requestNextPage: ({
    startIndex,
    stopIndex
  }: {
    startIndex: number
    stopIndex: number
  }) => Promise<any>
  markNotificationRead: (args: MarkNotificationReadProps) => void
  markAllNotificationsRead: (onError?: (e: Error) => void) => void
  isMarkAsReadLoading: boolean
  isMarkAllAsReadLoading: boolean
}

type UseNotificationsData = ({
  organizationUuid,
  enabled
}: {
  organizationUuid: string
  enabled: boolean
}) => UseNotificationsDataResult

export interface CacheNewDataPayload {
  newNotifications: NotificationData[]
  newNotificationsCount: number
}

export interface CachePageDataPayload {
  notifications: NotificationData[]
  remoteCount: number
}

type ReducerAction =
  | { type: 'RESET' }
  | { type: 'LOADING' }
  | {
      type: 'CACHE_PAGE_DATA'
      payload: CachePageDataPayload
    }
  | {
      type: 'CACHE_NEW_DATA'
      payload: CacheNewDataPayload
    }
  | {
      type: 'PAGE_REQUEST_ERROR'
    }
  | {
      type: 'MARK_AS_READ'
      payload: string
    }
  | {
      type: 'MARK_AS_UNREAD'
      payload: string
    }
  | { type: 'MARK_ALL_AS_READ' }
  | { type: 'RESET_MARK_ALL_AS_READ'; payload: null | number }

const handleError = (error) => {
  console.error(error)
}

// Reducer for caching new notifications data
export const cacheNewDataReducer = (
  state: State,
  {
    payload: { newNotifications, newNotificationsCount }
  }: {
    payload: CacheNewDataPayload
  }
) => ({
  ...state,
  cachedNotifications: [...newNotifications, ...state.cachedNotifications],
  lastNotificationTimestamp: newNotifications[0].timestamp,
  remoteCount: state.remoteCount + newNotificationsCount
})

// Reducer for caching paged notifications data
export const cachePageDataReducer = (
  { cachedNotifications, ...state }: State,
  {
    payload: { notifications, remoteCount }
  }: {
    payload: CachePageDataPayload
  }
) => {
  const newCache = [...cachedNotifications, ...notifications]
  return {
    ...state,
    cachedNotifications: newCache,
    remoteCount,
    lastNotificationTimestamp: newCache.length ? newCache[0].timestamp : null,
    isLoading: false
  }
}

const reducer = (state: State, action: ReducerAction) => {
  switch (action.type) {
    case 'RESET':
      return initialState
    case 'LOADING':
      return {
        ...state,
        isLoading: true
      }
    case 'CACHE_NEW_DATA':
      return cacheNewDataReducer(state, action)
    case 'CACHE_PAGE_DATA':
      return cachePageDataReducer(state, action)
    case 'PAGE_REQUEST_ERROR':
      return {
        ...state,
        pageRequestError: true
      }
    case 'MARK_AS_READ':
      return {
        ...state,
        cachedNotifications: markRead(action.payload, [
          ...state.cachedNotifications
        ])
      }
    case 'MARK_AS_UNREAD':
      return {
        ...state,
        cachedNotifications: markUnread(action.payload, [
          ...state.cachedNotifications
        ])
      }
    case 'MARK_ALL_AS_READ':
      return {
        ...state,
        markAllAsReadTimestamp: new Date().getTime()
      }
    case 'RESET_MARK_ALL_AS_READ':
      return {
        ...state,
        markAllAsReadTimestamp: action.payload
      }
  }
}

const markRead = (
  notificationId: string,
  notifications: NotificationData[]
): NotificationData[] => {
  const index = notifications.findIndex((n) => n.id === notificationId)
  if (index > -1) {
    const newNotification = { ...notifications[index], read: true }
    notifications[index] = newNotification
  }
  return notifications
}

const markUnread = (
  notificationId: string,
  notifications: NotificationData[]
): NotificationData[] => {
  const index = notifications.findIndex((n) => n.id === notificationId)
  if (index > -1) {
    const newNotification = { ...notifications[index], read: false }
    notifications[index] = newNotification
  }
  return notifications
}

export const useNotificationsData: UseNotificationsData = ({
  organizationUuid,
  enabled
}: {
  organizationUuid: string
  enabled: boolean
}) => {
  const [state, dispatch] = useReducer(reducer, initialState)
  const {
    mutate: mutateUpdateNotificationRead,
    isLoading: isMarkAsReadLoading
  } = useContrastMutation(updateNotificationRead, {
    onMutate: (mutationVariables) => {
      dispatch({
        type: 'MARK_AS_READ',
        payload: mutationVariables.notificationId as string
      })
    }
  })
  const {
    mutate: mutateUpdateAllNotificationsRead,
    isLoading: isMarkAllAsReadLoading
  } = useContrastMutation(updateNotificationRead, {
    onMutate: () => {
      const previousMarkAllAsReadTimestamp = state.markAllAsReadTimestamp
      dispatch({ type: 'MARK_ALL_AS_READ' })
      return { previousMarkAllAsReadTimestamp }
    }
  })
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!enabled) {
      dispatch({ type: 'RESET' })
    }
  }, [enabled])

  // Get "new" notifications every 15 seconds while menu is open
  // and if the "last" notification timestamp has already been cached
  const [newDataEnabled, setNewDataEnabled] = useState(true)
  const { data: newNotificationsData, ...newNotificationsResponse } =
    useContrastQuery(
      [Query.NEW_NOTIFICATIONS, organizationUuid],
      getNewNotifications,
      {
        organizationUuid,
        lastNotificationTimestamp: state.lastNotificationTimestamp
      },
      {
        enabled:
          newDataEnabled && enabled && state.lastNotificationTimestamp !== null,
        refetchInterval: 15000
      }
    )

  // When "new data" query returns an error, log to console and disable the query
  useEffect(() => {
    if (newNotificationsResponse.error) {
      console.error(newNotificationsResponse.error)
      setNewDataEnabled(false)
    }
  }, [newNotificationsResponse.error])

  useEffect(() => {
    if (newNotificationsData && newNotificationsData.count) {
      dispatch({
        type: 'CACHE_NEW_DATA',
        payload: {
          newNotifications: newNotificationsData.notifications,
          newNotificationsCount: newNotificationsData.count
        }
      })
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      queryClient.refetchQueries(
        [Query.NEW_NOTIFICATIONS_COUNT, organizationUuid],
        { exact: true }
      )
    }
  }, [newNotificationsData, organizationUuid, queryClient])

  const sendRequest = async ({
    offset,
    limit
  }: {
    offset: number
    limit: number
  }) => {
    if (state.pageRequestError || !enabled) {
      return Promise.resolve()
    }
    try {
      dispatch({ type: 'LOADING' })

      const { notifications, count: updatedRemoteCount } =
        await getNotificationsPage.call(
          { connector },
          {
            organizationUuid,
            limit,
            offset
          }
        )

      dispatch({
        type: 'CACHE_PAGE_DATA',
        payload: {
          notifications,
          remoteCount: updatedRemoteCount
        }
      })
    } catch (error) {
      console.error(error)
      dispatch({ type: 'PAGE_REQUEST_ERROR' })
    }
  }

  // This will get the first page when the menu is opened
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    sendRequest({
      offset: 0,
      limit: 10
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled])

  const requestNextPage: UseNotificationsDataResult['requestNextPage'] = ({
    startIndex,
    stopIndex
  }) =>
    sendRequest({
      offset: startIndex,
      limit: stopIndex - startIndex + 1
    })

  const markNotificationRead = useCallback(
    ({
      notificationId,
      onErrorHandler = handleError
    }: MarkNotificationReadProps) => {
      mutateUpdateNotificationRead(
        { organizationUuid, notificationId },
        {
          onSettled: (_, error, mutationVariables, _context) => {
            if (error) {
              dispatch({
                type: 'MARK_AS_UNREAD',
                payload: mutationVariables.notificationId as string
              })
              onErrorHandler(error as Error)
            }
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            queryClient.refetchQueries(
              [Query.NEW_NOTIFICATIONS_COUNT, organizationUuid],
              { exact: true }
            )
          }
        }
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [organizationUuid]
  )

  const markAllNotificationsRead = useCallback(
    (onErrorHandler = handleError) => {
      mutateUpdateAllNotificationsRead(
        { organizationUuid },
        {
          onSettled: (
            _,
            error,
            _mutationVariables,
            { previousMarkAllAsReadTimestamp }
          ) => {
            if (error) {
              dispatch({
                type: 'RESET_MARK_ALL_AS_READ',
                payload: previousMarkAllAsReadTimestamp
              })
              onErrorHandler(error as Error)
            }
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            queryClient.refetchQueries(
              [Query.NEW_NOTIFICATIONS_COUNT, organizationUuid],
              { exact: true }
            )
          }
        }
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [organizationUuid]
  )

  return {
    notifications: state.cachedNotifications,
    remoteCount: state.remoteCount,
    requestNextPage,
    markNotificationRead,
    markAllNotificationsRead,
    isLoading: state.isLoading,
    isMarkAsReadLoading,
    isMarkAllAsReadLoading,
    markAllAsReadTimestamp: state.markAllAsReadTimestamp
  }
}
